<template>
  <div>
    <vue-apex-charts
      type="donut"
      height="350"
      :options="chartOptions"
      :series="series"
    />
    <b-container class="mt-1">
      <b-row>
        <b-col md="6" class="d-flex flex-column align-items-center">
          <div class="h4">
            Q{{ previousQuartal.quartal }} {{ currentYear }}
          </div>
          <div class="h5">
            {{ previousQuartal.rate }}
          </div>
        </b-col>
        <b-col md="6" class="d-flex flex-column align-items-center">
          <div class="h4">
            FY {{ currentYear }}
          </div>
          <div class="h5">
            {{ totalYear }}
          </div>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { ref } from "@vue/composition-api"; // <--- PENTING: import ref
import { BContainer, BRow, BCol } from "bootstrap-vue";
import VueApexCharts from "vue-apexcharts";
import * as Services from "./services";

export default {
  components: {
    BContainer,
    BRow,
    BCol,
    VueApexCharts,
  },

  setup() {
    const { Rating } = Services;
    const { data: dataRating } = Rating();

    // cache semua records yang pernah datang
    const allRecords = ref([]);

    return {
      dataRating,
      allRecords,
    };
  },

  data() {
    return {
      previousQuartal: {
        quartal: 1,
        rate: 0,
        total_distractor: 0,
        total_passive: 0,
        total_promotors: 0,
      },
      totalYear: 0,
      currentYear: new Date().getFullYear(),
      series: [0, 0, 0],
      chartOptions: {
        legend: {
          show: true,
          position: "right",
          fontSize: "14px",
          fontFamily: "Montserrat",
        },
        chart: {
          toolbar: {
            show: false,
          },
        },
        colors: ["#ffe700", "#00d4bd", "#826bf8"],
        dataLabels: {
          enabled: true,
          formatter(val) {
            return `${parseInt(val)}%`;
          },
        },
        plotOptions: {
          pie: {
            donut: {
              labels: {
                show: true,
                name: {
                  fontSize: "2rem",
                  fontFamily: "Montserrat",
                },
                value: {
                  fontSize: "1rem",
                  fontFamily: "Montserrat",
                  formatter(val) {
                    return `${parseInt(val)}%`;
                  },
                },
                total: {
                  show: true,
                  fontSize: "1.5rem",
                  label: "Rate",
                  formatter() {
                    return 0;
                  },
                },
              },
            },
          },
        },
        labels: ["Detractors", "Passives", "Promoters"],
        responsive: [
          {
            breakpoint: 992,
            options: {
              chart: { height: 380 },
              legend: { position: "right" },
            },
          },
          {
            breakpoint: 576,
            options: {
              chart: { height: 320 },
              plotOptions: {
                pie: {
                  donut: {
                    labels: {
                      show: true,
                      name: { fontSize: "1.5rem" },
                      value: { fontSize: "1rem" },
                      total: { fontSize: "1.5rem" },
                    },
                  },
                },
              },
              legend: { position: "right" },
            },
          },
        ],
      },
    };
  },

  watch: {
    // dataRating di-return dari setup(), jadi tersedia di this.dataRating
    dataRating(val) {
      // jika dataRating ada dan berisi records, merge ke cache
      if (Array.isArray(val) && val.length) {
        val.forEach((rec) => {
          // pastikan quartal numeric
          const q = Number(rec.quartal);
          const idx = this.allRecords.findIndex((r) => Number(r.quartal) === q);
          if (idx !== -1) {
            // replace agar reaktif dan tidak duplikat
            this.allRecords.splice(idx, 1, rec);
          } else {
            this.allRecords.push(rec);
          }
        });
      }

      // gunakan cache (allRecords) untuk render — jangan paksa overwrite cache kalau val kosong
      const data = (this.allRecords || []).slice().sort((a, b) => Number(a.quartal) - Number(b.quartal));
      if (!data.length) {
        // kalau cache kosong, tidak ada yg bisa ditampilkan
        this.series = [0, 0, 0];
        this.previousQuartal = { quartal: 1, rate: 0, total_distractor: 0, total_passive: 0, total_promotors: 0 };
        this.totalYear = 0;
        return;
      }

      // cari currentQData: prefer last record with rate !== 0, fallback ke last record
      const lastNonZero = [...data].reverse().find((r) => Number(r.rate || 0) !== 0);
      const lastRecord = data[data.length - 1];
      const currentQData = lastNonZero || lastRecord;
      const currentQ = Number(currentQData.quartal || lastRecord.quartal);

      // hitung FY average berdasarkan data cache (hitung only non-zero rates)
      let total = 0;
      let divideBy = 0;
      data.forEach((d) => {
        const r = Number(d.rate || 0);
        total += r;
        if (r !== 0) divideBy++;
      });
      this.totalYear = divideBy > 0 ? Math.ceil(total / divideBy) : 0;

      // series dari currentQData (fallback ke 0 jika missing)
      this.series = [
        Number(currentQData.total_distractor || 0),
        Number(currentQData.total_passive || 0),
        Number(currentQData.total_promotors || 0),
      ];

      // update donut center total
      this.chartOptions = {
        ...this.chartOptions,
        plotOptions: {
          ...this.chartOptions.plotOptions,
          pie: {
            ...this.chartOptions.plotOptions.pie,
            donut: {
              ...this.chartOptions.plotOptions.pie.donut,
              labels: {
                ...this.chartOptions.plotOptions.pie.donut.labels,
                total: {
                  ...this.chartOptions.plotOptions.pie.donut.labels.total,
                  formatter: () => Number(currentQData.rate || 0),
                },
              },
            },
          },
        },
      };

      // previous quartal: cari quartal currentQ - 1 di cache,
      // jika tidak ada, cari nearest lower quartal
      const prevQ = currentQ === 1 ? 1 : currentQ - 1;
      let prevData = data.find((e) => Number(e.quartal) === prevQ);
      if (!prevData) {
        // cari nearest lower quartal than currentQ
        prevData = [...data].reverse().find((e) => Number(e.quartal) < currentQ) || {
          quartal: prevQ,
          rate: 0,
          total_distractor: 0,
          total_passive: 0,
          total_promotors: 0,
        };
      }

      this.previousQuartal = {
        quartal: Number(prevData.quartal || prevQ),
        rate: Number(prevData.rate || 0),
        total_distractor: Number(prevData.total_distractor || 0),
        total_passive: Number(prevData.total_passive || 0),
        total_promotors: Number(prevData.total_promotors || 0),
      };

      // update currentYear jika record punya year field (opsional)
      if (currentQData.year) this.currentYear = currentQData.year;
    },
  },

  methods: {
    // masih kamu pakai; tidak diubah
    async getData() {
      return await this.$http
        .get(`/rating/case/result-admin/take/all/year/${this.currentYear}`)
        .then((res) => {
          let data = res.data.data.records;
          // inisialisasi cache kalau belum ada
          if (Array.isArray(data) && data.length) {
            data.forEach((rec) => {
              const idx = this.allRecords.findIndex((r) => Number(r.quartal) === Number(rec.quartal));
              if (idx !== -1) this.allRecords.splice(idx, 1, rec);
              else this.allRecords.push(rec);
            });
          }
        })
        .catch((err) => console.error(err));
    },

    updateTotal(total) {
      this.chartOptions = {
        ...this.chartOptions,
        plotOptions: {
          ...this.chartOptions.plotOptions,
          pie: {
            ...this.chartOptions.plotOptions.pie,
            donut: {
              ...this.chartOptions.plotOptions.pie.donut,
              labels: {
                ...this.chartOptions.plotOptions.pie.donut.labels,
                total: {
                  ...this.chartOptions.plotOptions.pie.donut.labels.total,
                  formatter() {
                    return total;
                  },
                },
              },
            },
          },
        },
      };
    },
  },

  mounted() {
    // jika service belum mengisi cache, panggil fallback getData()
    // (ini menjaga behavior lama saat user belum pakai date filter)
    if (!this.allRecords || !this.allRecords.length) {
      this.getData();
    }
  },
};
</script>

<style>
.apexcharts-menu-item.exportSVG {
  display: none;
}
</style>
